import "./cards-layout.css";
import React, { useEffect, useState } from "react";
import PlanCard from "./components/PlanCard";
import Register from "./components/Register";
import { Plan } from "core/interfaces/plan.interface";
import LAlert from "components/LAlert";
import { IPeriodicTime } from "core/enums/plans.enum";
import { useLandingPageContext } from "pages/Context/LandinPageContext";
import { GetPlans } from "./service/payment.service";

const monthsByPeriod: Partial<Record<IPeriodicTime, number>> = {
  [IPeriodicTime.BIMONTHLY]: 2,
  [IPeriodicTime.QUARTERLY]: 3,
  [IPeriodicTime.SEMESTERLY]: 6,
  [IPeriodicTime.YEARLY]: 12,
};

export interface PlanWithDiscount extends Plan {
  discount: string;
  savingsAmount?: number;
  savingsPercentage?: number;
  monthlyAmountCurrency?: string;
  allAmountCurrency?: string;
}

const Plans: React.FC = () => {
  const { setSelectedPlanContext, setPlansContext } = useLandingPageContext();
  const [plans, setPlans] = useState<PlanWithDiscount[]>([]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response: Plan[] = await GetPlans();
        const montlyPlan = response.find(plan => plan.period.type === IPeriodicTime.MONTHLY);
        
        const responseWithDiscount: PlanWithDiscount[] = response
          .filter(plan => plan.period.type !== IPeriodicTime.FREE)
          .map(plan => {
          let discount = "0";
          let savingsAmount = 0;
          let savingsPercentage = 0;

          const planFormatted = {
            monthlyAmountCurrency: `R$ ${plan.monthlyAmount.toFixed(2).replace(".", ",")}`,
            allAmountCurrency: `R$ ${plan.allAmount.toFixed(2).replace(".", ",")}`,
            ...plan,
          };

          const months = monthsByPeriod[plan.period.type];
          if (montlyPlan && months) {
            const referenceAmount = montlyPlan.monthlyAmount * months;
            const discountNumber = referenceAmount > 0
              ? Math.round((1 - plan.allAmount / referenceAmount) * 100)
              : 0;
            if (Number.isFinite(discountNumber) && discountNumber > 0) {
              discount = `${discountNumber}% de economia`;
              savingsAmount = Math.round((referenceAmount - plan.allAmount) * 100) / 100;
              savingsPercentage = (1 - plan.allAmount / referenceAmount) * 100;
            }
          }

          if(plan.isMarked) {
            setSelectedPlanContext({ ...planFormatted, discount, savingsAmount });
          }

          return { ...planFormatted, discount, savingsAmount, savingsPercentage };
        }).sort((a, b) => a.priority - b.priority);

        setPlans(responseWithDiscount);
        setPlansContext(responseWithDiscount);
      } catch (error) {
        LAlert({ description: `${error}` });
      }
    };

    fetchPlans();
  }, [setPlansContext, setSelectedPlanContext]);

  const benefitOrder = Array.from(new Set(plans.flatMap(plan => plan.benefit ?? [])));
  const recommendedPlan = plans.reduce<PlanWithDiscount | undefined>((best, plan) => {
    const percentage = plan.savingsPercentage ?? 0;
    if (percentage <= 0) return best;
    if (!best || percentage > (best.savingsPercentage ?? 0)) return plan;
    if (percentage === best.savingsPercentage && (plan.savingsAmount ?? 0) > (best.savingsAmount ?? 0)) return plan;
    return best;
  }, undefined);

  return (
    <div id="pricings" className="mx-auto w-full min-h-screen h-full pt-20 bg-white px-0">
      <div className="text-center">
        <h2 className="text-2xl sm:text-4xl font-semibold text-gray-800 mb-4">Planos</h2>
        <p className="font-new-order font-normal text-gray-600 mb-12 text-sm sm:text-base">
          Escolha o plano ideal para você e comece a simplificar suas licitações hoje mesmo!
        </p>

        <div className="plans-comparison-grid">
          {plans.length > 0 &&
            plans.map((plan) => <PlanCard key={plan._id} plan={plan} benefitOrder={benefitOrder} isRecommended={plan._id === recommendedPlan?._id} />)}
        </div>
      </div>
      <Register />
    </div>
  );
};

export default Plans;
